import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import MarketSidebar, { selectedStoreKeyType } from '@/containers/productShipment/MarketSidebar';
import { useAppSelector } from '@/redux/store';
import { cn } from '@/utils/tailwind';
import { useCallback, useEffect, useRef, useState } from 'react';
import InquiryListContainer from '@/containers/inquiry/InquiryListContainer';
import { Inquiry, Status } from '@/types/inquiry';
import moment from 'moment';
import { useToast } from '@/hooks/useToast';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { useLog } from '@/hooks/V3/useLog';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import { PullToRefresh } from '@/components/molecules/PullToRefresh';
import {
  createMobileInquiryPreviewData,
  type MobileInquiryPreviewStore,
} from '@/pages/Inquiry/membership-inquiry-preview';

const InquiryListPage = () => {
  const { toast } = useToast();
  const user = useAppSelector((state) => state.user);
  const isPreviewMode = useMembershipPreviewMode();
  const { setUserEventHistory } = useLog(user.UserNo);
  const [storeList, setStoreList] = useState<Array<MobileInquiryPreviewStore>>([]);
  const [selectedStoreKey, setSelectedStoreKey] = useState<selectedStoreKeyType>({
    MarketId: '0',
    StoreId: '',
  });
  const [activedStatus, setActivedStatus] = useState<Status>('신규문의');
  const stutusList: Array<Status> = ['신규문의', '처리완료'];
  const [inquiryList, setInquiryList] = useState<Array<Inquiry>>([]);
  const [unCheckedInquiryList, setUnCheckedInquiryList] = useState<Array<Inquiry>>([]);
  const [checkedInquiryList, setCheckedInquiryList] = useState<Array<Inquiry>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const selectedStoreIsReleasedStatus =
    storeList.find((item) => item.StoreId === selectedStoreKey.StoreId)?.IsReleasedStatus || false;
  const deviceUuid = useRecoilValue(deviceUuidState);

  const { mutate: getInquiryList, isPending: isPendingGetInquiryList } = usePost(
    CPLAT_API_ROUTES.getInquiryList,
  );

  const handleInquiryList = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (isPreviewMode) {
        const { StoreList, InquiryList } = createMobileInquiryPreviewData();

        setStoreList(StoreList);
        setInquiryList(InquiryList);
        setIsLoading(false);
        resolve();
        return;
      }

      getInquiryList(
        {
          cplatToken: user.CplatToken,
          dateFrom: moment(new Date()).subtract(6, 'days').format('YYYY-MM-DD'),
          dateTo: moment(new Date()).format('YYYY-MM-DD'),
        },
        {
          onSuccess(res) {
            const code = res.data.code;
            if (code === '200') {
              const { StoreList, InquiryList } = res.data.data[0];
              setStoreList(StoreList);
              setInquiryList(InquiryList);
            }
          },
          onSettled() {
            setIsLoading(false);
            resolve();
          },
        },
      );
    });
  }, [getInquiryList, isPreviewMode, user.CplatToken]);

  const getActivedList = (selectedStoreKey: selectedStoreKeyType, inquiryList: Array<Inquiry>) => {
    if (selectedStoreKey.MarketId === '0' && selectedStoreKey.StoreId === '') {
      return inquiryList;
    } else {
      const filteredList = inquiryList.filter(
        (item: Inquiry) =>
          item.StoreId === selectedStoreKey.StoreId &&
          String(item.MarketId) === selectedStoreKey.MarketId,
      );
      return filteredList;
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);

  useEffect(() => {
    handleInquiryList();
  }, [handleInquiryList]);

  useEffect(() => {
    const activedList = getActivedList(selectedStoreKey, inquiryList);
    const unCheckedInquiryList = activedList.filter((item: Inquiry) => item.CheckedYn === 'N');
    const checkedInquiryList = activedList.filter((item: Inquiry) => item.CheckedYn === 'Y');
    setUnCheckedInquiryList(unCheckedInquiryList);
    setCheckedInquiryList(checkedInquiryList);
  }, [selectedStoreKey, inquiryList]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data['updateMarketData']) {
        setSelectedStoreKey({
          MarketId: '0',
          StoreId: '',
        });
        const text = data['updateMarketData'];
        if (text === 'Y') {
          toast({
            icon: '✅',
            title: '답변 등록이 완료되었어요',
            variant: 'dark',
          });
          handleInquiryList();
        } else {
          toast({
            icon: '⚠️',
            title: text,
            variant: 'dark',
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage as EventListener);
    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage as EventListener);
    };
  }, [handleInquiryList, toast]);

  return (
    <div
      className="relative"
      data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
    >
      {/* 초기 로딩시에만 GlobalSpinner */}
      {isPendingGetInquiryList && isLoading && <GlobalSpinner />}

      <main className="flex h-screen flex-col">
        <div className="flex-shrink-0">
          <MarketSidebar
            storeList={storeList}
            selectedStoreKey={selectedStoreKey}
            setSelectedStoreKey={setSelectedStoreKey}
            enableTotal={true}
          />
          <div className="flex items-center justify-between bg-gr-50">
            <div className="flex gap-[12px] px-[20px] py-[12px]">
              {stutusList.map((item) => (
                <div
                  key={item}
                  onClick={() => {
                    setActivedStatus(item);
                    setUserEventHistory({
                      eventName: 'select_inquiry_state',
                      eventValue: item,
                      deviceUuid,
                    });
                  }}
                  className={cn(
                    activedStatus === item ? 'text-gr-900 Body4S14' : 'text-gr-600 Body5M14',
                  )}
                >
                  <span>
                    {item}{' '}
                    {item === '신규문의' ? unCheckedInquiryList.length : checkedInquiryList.length}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-grow flex-col bg-white">
          <PullToRefresh
            ref={containerRef}
            refreshing={refreshing}
            setRefreshing={setRefreshing}
            onRefresh={handleInquiryList}
          >
            <InquiryListContainer
              activedStatus={activedStatus}
              list={activedStatus === '신규문의' ? unCheckedInquiryList : checkedInquiryList}
              isLoading={isLoading}
              IsReleasedStatus={selectedStoreIsReleasedStatus}
              handleInquiryList={handleInquiryList}
            />
          </PullToRefresh>
        </div>
      </main>
    </div>
  );
};

export default InquiryListPage;
